import { useTranslation } from "react-i18next";
import AttendanceSelector from "./AttendanceSelector";
import Field from "./Field";

// ─── Helpers ───────────────────────────────────────────────────

function findResponse(responses, memberId, sectionId) {
  return responses.find(
    (r) => r.memberId === memberId && r.sectionId === sectionId,
  );
}

// Only render the venue line when it adds information — avoids the
// "Idilofon / Idilofon" redundancy when the section is named after its
// venue.
function shouldShowVenue(section) {
  if (!section.venue) return false;
  return section.venue.trim().toLowerCase() !== section.name?.trim().toLowerCase();
}

// ─── Styles ────────────────────────────────────────────────────

const SECTION_CARD_STYLE = {
  background: "rgba(0,0,0,0.03)",
  border: "1px solid rgba(0,0,0,0.08)",
  borderRadius: 14,
  padding: "18px 22px",
  marginBottom: 16,
};

// Grid layout keeps the name column and the dropdown column on fixed
// tracks across every row, regardless of whether the name cell is a
// read-only label, an inline input, or has a trailing × remove button.
// Result: dropdowns in all rows stay perfectly right-aligned.
//
// Columns: [name — flexible] [dropdown — auto] [remove × — 24px reserve]
const MEMBER_ROW_STYLE = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) auto 24px",
  alignItems: "center",
  gap: 12,
  padding: "6px 0",
};

// ─── Component ─────────────────────────────────────────────────

/**
 * AttendanceMatrix
 *
 * Renders one section card per event section, each listing every member
 * with an AttendanceSelector. Shared between the coded and request-to-
 * attend flows — both pass the same shape of `responses` (array of
 * { memberId, sectionId, status }) and a matching onChange callback.
 *
 * Props:
 *   sections    — EventSection[] with _id, name, and optional venue
 *   members     — { _id, name, isNew? }[]
 *   responses   — { memberId, sectionId, status }[]
 *   onChange    — (memberId, sectionId, nextStatus) => void
 *   disabled    — disables every selector while a POST is in flight
 *   footer              — optional render slot tucked inside the last
 *                         section card (used for the "+ Add another guest"
 *                         link so it feels anchored rather than orphaned
 *                         in empty space)
 *   onMemberNameChange  — optional. (memberId, nextName) => void. When
 *                         provided AND the member's id starts with
 *                         "new-", the name cell becomes an inline
 *                         editable input. Used by the coded flow so
 *                         added guests are edited inline instead of
 *                         via a separate list below the matrix.
 *   onMemberRemove      — optional. (memberId) => void. When provided,
 *                         new-members get a trailing × remove button.
 */
const AttendanceMatrix = ({
  sections = [],
  members = [],
  responses = [],
  onChange,
  disabled = false,
  footer = null,
  onMemberNameChange = null,
  onMemberRemove = null,
}) => {
  const { t } = useTranslation();
  const inlineEditable = typeof onMemberNameChange === "function";
  if (sections.length === 0 || members.length === 0) return null;

  const lastSectionIndex = sections.length - 1;

  return (
    <div>
      {sections.map((section, sectionIdx) => {
        const showVenue = shouldShowVenue(section);
        const description = (section.description || "").trim();
        const rsvpRequired = section.isRSVPRequired !== false;
        const hasHeaderSpacingBelow = showVenue || description;
        return (
        <div key={section._id} style={SECTION_CARD_STYLE}>
          <p
            className="body-16"
            style={{
              fontWeight: 600,
              marginBottom: hasHeaderSpacingBelow ? 2 : 10,
              letterSpacing: 0.2,
            }}
          >
            {section.name}
          </p>
          {showVenue && (
            <p
              className="body-16"
              style={{
                opacity: 0.55,
                marginBottom: description ? 6 : 10,
                fontSize: 13,
              }}
            >
              {section.venue}
            </p>
          )}
          {description && (
            <p
              className="body-16"
              style={{
                opacity: 0.75,
                marginBottom: 12,
                fontSize: 14,
                lineHeight: 1.55,
                whiteSpace: "pre-wrap",
              }}
            >
              {description}
            </p>
          )}
          {rsvpRequired && members.map((member) => {
            const r = findResponse(responses, member._id, section._id);
            const status = r?.status || "pending";
            const editable = inlineEditable && member.isNew;
            return (
              <div key={member._id} style={MEMBER_ROW_STYLE}>
                {editable ? (
                  <Field
                    type="text"
                    placeholder={t("rsvp.guestNamePlaceholder")}
                    value={member.rawName ?? member.name ?? ""}
                    onChange={(e) =>
                      onMemberNameChange(member._id, e.target.value)
                    }
                    disabled={disabled}
                    aria-label={t("rsvp.guestNameAriaLabel")}
                    style={{
                      minWidth: 0,
                      width: "100%",
                    }}
                  />
                ) : (
                  <span
                    className="body-16"
                    style={{
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {member.name}
                    {member.isNew && (
                      <span style={{ opacity: 0.5 }}>{t("rsvp.newGuestSuffix")}</span>
                    )}
                  </span>
                )}
                <AttendanceSelector
                  value={status}
                  onChange={(next) =>
                    onChange?.(member._id, section._id, next)
                  }
                  memberName={member.name}
                  sectionName={section.name}
                  disabled={disabled}
                />
                {editable && onMemberRemove ? (
                  <button
                    type="button"
                    onClick={() => onMemberRemove(member._id)}
                    disabled={disabled}
                    aria-label={t("rsvp.removeGuestAriaLabel")}
                    style={{
                      background: "none",
                      border: "none",
                      color: "var(--black)",
                      cursor: disabled ? "not-allowed" : "pointer",
                      fontSize: 20,
                      lineHeight: 1,
                      opacity: disabled ? 0.5 : 0.7,
                      padding: 0,
                      justifySelf: "center",
                    }}
                  >
                    &times;
                  </button>
                ) : (
                  // Reserve the third column so dropdowns stay aligned
                  // across rows that don't have a remove button.
                  <span aria-hidden="true" />
                )}
              </div>
            );
          })}
          {footer && sectionIdx === lastSectionIndex && (
            <div
              style={{
                marginTop: 14,
                paddingTop: 12,
                borderTop: "1px dashed rgba(0,0,0,0.12)",
              }}
            >
              {footer}
            </div>
          )}
        </div>
        );
      })}
    </div>
  );
};

export default AttendanceMatrix;
